/**
 * 計助: 計算式ドキュメント（FORMULAS.md）の閲覧
 * リポジトリ docs を Vite エイリアス @keisukeDocs 経由でバンドルする。
 * # / ## / ### 見出しから簡易目次を生成し、同一ページ内でスクロールする。
 */

import { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import formulasMarkdown from '@keisukeDocs/FORMULAS.md?raw';
import { buildFormulaTocAndContent } from './KeisukeFormulasPage.toc';

export default function KeisukeFormulasPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { toc, contentLines } = useMemo(() => buildFormulaTocAndContent(formulasMarkdown), []);

  const scrollTo = useCallback((id: string) => {
    document.getElementById(`keisuke-formula-${id}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  return (
    <Container fluid className="py-3" style={{ maxWidth: 1100 }}>
      <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
        <h1 className="h4 mb-0">
          {t('keisuke.formulasPage.title')}
        </h1>
        <Button type="button" variant="outline-primary" size="sm" onClick={() => navigate('/keisuke')}>
          {t('keisuke.formulasPage.backToDashboard')}
        </Button>
      </div>
      <p className="text-muted small mb-3">
        {t('keisuke.formulasPage.lead')}
      </p>
      <Row className="g-3">
        <Col lg={3}>
          <div className="small fw-semibold mb-2 border-bottom pb-1">
            {t('keisuke.formulasPage.tocTitle')}
          </div>
          {toc.length === 0 ? (
            <p className="text-muted small mb-0">{t('keisuke.formulasPage.tocEmpty')}</p>
          ) : (
            <ul className="list-unstyled small mb-0">
              {toc.map((item) => (
                <li key={item.id} className="mb-1" style={{ paddingLeft: (item.level - 1) * 10 }}>
                  <Button
                    type="button"
                    variant="link"
                    className="p-0 text-start text-decoration-none"
                    onClick={() => scrollTo(item.id)}
                  >
                    {item.title}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Col>
        <Col lg={9}>
          <Card>
            <Card.Body className="p-0">
              <div
                className="mb-0 px-3 py-3 small font-monospace"
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 'calc(100vh - 220px)',
                  overflow: 'auto',
                }}
                data-keisuke-formulas-md
              >
                {contentLines.map((row, idx) =>
                  row.kind === 'heading' ? (
                    <span
                      key={`h-${idx}-${row.id}`}
                      id={`keisuke-formula-${row.id}`}
                      className={`d-block fw-semibold text-body ${row.level <= 1 ? 'mt-3 fs-6' : 'mt-2'}`}
                    >
                      {row.text}
                      {'\n'}
                    </span>
                  ) : (
                    <span key={`t-${idx}`}>
                      {row.text}
                      {'\n'}
                    </span>
                  )
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
